import { useCallback, useReducer, useRef } from 'react'
import { apiFetch, ApiError } from '@/api/client'
import { useAuthContext } from '@/auth/AuthProvider'

/**
 * Mutation hook for endpoints whose path is only known at call time.
 *
 * `useApiMutation` binds a path when the hook is constructed, which works
 * for `POST /api/initiatives` and stops working the moment the path carries
 * an id the caller picks per click — editing comment 7 versus comment 9,
 * clearing G1 versus G2. Stage 13 added six such endpoints, at which point
 * copying the reducer into each hook (the `useUpdateInitiative` /
 * `useDeleteInitiative` pattern) stopped being cheaper than this.
 *
 * Never throws: `run` resolves to `null` on failure and the error is on
 * `state`, so callers can chain without try/catch.
 */

export type ActionState<T> =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; data: T }
  | { status: 'error'; error: ApiError | Error }

type ActionEvent<T> =
  | { type: 'RESET' }
  | { type: 'FETCH' }
  | { type: 'SUCCESS'; data: T }
  | { type: 'ERROR'; error: ApiError | Error }

function reducer<T>(state: ActionState<T>, event: ActionEvent<T>): ActionState<T> {
  switch (event.type) {
    case 'RESET':
      return { status: 'idle' }
    case 'FETCH':
      return { status: 'loading' }
    case 'SUCCESS':
      return { status: 'success', data: event.data }
    case 'ERROR':
      return { status: 'error', error: event.error }
    default:
      return state
  }
}

export interface UseApiActionResult<TResult> {
  state: ActionState<TResult>
  run: (
    path: string,
    method: 'POST' | 'PATCH' | 'PUT' | 'DELETE',
    body?: unknown,
  ) => Promise<TResult | null>
  reset: () => void
}

export function useApiAction<TResult>(): UseApiActionResult<TResult> {
  const { getToken } = useAuthContext()
  const [state, dispatch] = useReducer(
    reducer as (s: ActionState<TResult>, e: ActionEvent<TResult>) => ActionState<TResult>,
    { status: 'idle' },
  )

  const getTokenRef = useRef(getToken)
  getTokenRef.current = getToken

  // Per-mount sequence number so a fast double-click doesn't let the
  // slower response overwrite the faster one.
  const seqRef = useRef(0)

  const run = useCallback<UseApiActionResult<TResult>['run']>(async (path, method, body) => {
    const seq = ++seqRef.current
    dispatch({ type: 'FETCH' })
    try {
      const result = await apiFetch<TResult>(path, () => getTokenRef.current(), {
        method,
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      })
      if (seq !== seqRef.current) return null
      dispatch({ type: 'SUCCESS', data: result })
      return result
    } catch (err) {
      if (seq !== seqRef.current) return null
      dispatch({
        type: 'ERROR',
        error: err instanceof Error ? err : new Error(String(err)),
      })
      return null
    }
  }, [])

  const reset = useCallback(() => dispatch({ type: 'RESET' }), [])

  return { state, run, reset }
}
